import { getChromaticScale, getNoteAtInterval } from './diatonicChords';

/**
 * Semitone intervals from root for each chord type.
 * Intervals >= 12 indicate notes that appear in the second octave
 * (e.g., add9 = 14 semitones = octave + major 2nd).
 */
const CHORD_INTERVALS = {
  major: [0, 4, 7],
  minor: [0, 3, 7],
  dim: [0, 3, 6],
  aug: [0, 4, 8],
  sus2: [0, 2, 7],
  sus4: [0, 5, 7],
  5: [0, 7, 12], // power chord: root, fifth and the octave, no third
  7: [0, 4, 7, 10],
  m7: [0, 3, 7, 10],
  M7: [0, 4, 7, 11],
  maj7: [0, 4, 7, 11],
  dim7: [0, 3, 6, 9],
  m7b5: [0, 3, 6, 10],
  add2: [0, 2, 4, 7],
  add9: [0, 4, 7, 14],
  add11: [0, 4, 7, 17],
  madd2: [0, 2, 3, 7],
  madd9: [0, 3, 7, 14],
  madd11: [0, 3, 7, 17],
  9: [0, 4, 7, 10, 14],
  m9: [0, 3, 7, 10, 14],
  maj9: [0, 4, 7, 11, 14],
  6: [0, 4, 7, 9],
  m6: [0, 3, 7, 9],
  11: [0, 4, 7, 10, 14, 17],
  m11: [0, 3, 7, 10, 14, 17],
};

/**
 * How an inversion is written: a caret and the number of notes taken off the bottom and put on top.
 * 'C^1' is first inversion, 'C^2' second.
 *
 * The number is written rather than counted in marks because a chart is read at a glance, across a
 * room — '^2' and '^3' are told apart instantly where '^^' and '^^^' have to be counted.
 *
 * Deliberately not a slash chord. 'C/E' says to put an E in the bass — a note the chord gains,
 * under a body that stays where it is. '^' says only to turn the chord over, and adds nothing.
 * Two different instructions, so two different marks. A symbol can carry both.
 *
 * A bare digit was never available: this app already reads 'C2' as Cadd2 and 'C7' as a seventh, so
 * a number on its own is a quality and cannot also be an inversion.
 */
const INVERSION_MARK = /\^([1-3])$/;

/**
 * How an inversion is read out, and drawn: 'C^1' is shown as C with a raised "1st" after it.
 *
 * An ordinal rather than the bare figure because a bare one would be indistinguishable from the
 * figures a chord already carries — both are drawn raised, so 'C^1' and 'C7' would look like the
 * same kind of thing. Nothing else in a chord symbol reads as "1st".
 */
export const INVERSION_ORDINALS = ['', '1st', '2nd', '3rd'];

/** The inversion a symbol asks for, and the symbol with the mark taken off. */
export function splitInversion(chordStr) {
  const text = String(chordStr ?? '');
  const mark = text.match(INVERSION_MARK);

  return { symbol: mark ? text.slice(0, -mark[0].length) : text, inversion: mark ? Number(mark[1]) : 0 };
}

/**
 * Parse a chord string into its components.
 * Handles slash chords (e.g. G/B) by splitting at the first '/', and inversion marks (e.g. C^1).
 * Returns { root, quality, bassNote, inversion } or null if unparseable.
 */
export function parseChord(chordStr) {
  if (!chordStr || typeof chordStr !== 'string') return null;

  // Strip any HTML that may have leaked through
  const withMark = chordStr.replace(/<[^>]*>/g, '').trim();
  if (!withMark) return null;

  // The mark comes off before anything else reads the symbol: the quality chain below tests some
  // suffixes for equality, so a trailing caret would stop 'C6^' being read as the 6 it is
  const { symbol: cleaned, inversion } = splitInversion(withMark);
  if (!cleaned) return null;

  // Split slash chord — only the left side determines the chord tones
  const slashIdx = cleaned.indexOf('/');
  const mainChord = slashIdx !== -1 ? cleaned.slice(0, slashIdx) : cleaned;
  const bassNote = slashIdx !== -1 ? cleaned.slice(slashIdx + 1) || null : null;

  // Extract root note (letter + optional accidental)
  const rootMatch = mainChord.match(/^([A-G][#b]?)(.*)/);
  if (!rootMatch) return null;

  const root = rootMatch[1];
  const suffix = rootMatch[2];

  // Determine quality — ordered most-specific first to avoid mis-matches
  let quality = 'major';

  if (suffix.startsWith('m7b5')) quality = 'm7b5';
  else if (suffix.startsWith('maj9')) quality = 'maj9';
  else if (suffix.startsWith('maj7') || suffix.startsWith('M7')) quality = 'maj7';
  else if (suffix.startsWith('m11')) quality = 'm11';
  else if (suffix.startsWith('m9')) quality = 'm9';
  else if (suffix.startsWith('m7')) quality = 'm7';
  else if (suffix.startsWith('m6')) quality = 'm6';
  else if (suffix.startsWith('madd11')) quality = 'madd11';
  else if (suffix.startsWith('madd9')) quality = 'madd9';
  else if (suffix.startsWith('madd2')) quality = 'madd2';
  else if (suffix === 'm2') quality = 'madd2'; // shorthand, same as the bare '2' below
  else if (suffix.startsWith('min') || suffix.startsWith('m')) quality = 'minor';
  else if (suffix.startsWith('dim7')) quality = 'dim7';
  else if (suffix.startsWith('dim')) quality = 'dim';
  else if (suffix.startsWith('aug')) quality = 'aug';
  else if (suffix.startsWith('sus2')) quality = 'sus2';
  else if (suffix.startsWith('sus')) quality = 'sus4';
  else if (suffix.startsWith('add11')) quality = 'add11';
  else if (suffix.startsWith('add9')) quality = 'add9';
  else if (suffix.startsWith('add2')) quality = 'add2';
  else if (suffix === '11') quality = '11';
  else if (suffix === '9') quality = '9';
  else if (suffix === '7') quality = '7';
  else if (suffix === '6') quality = '6';
  else if (suffix === '5') quality = '5';
  // Bare '2' is chart shorthand for add2 — 'F2' is the same chord as 'Fadd2', never a bare
  // interval. Sits last so it can't shadow 'sus2', which spells its own quality out.
  else if (suffix === '2') quality = 'add2';

  return { root, quality, bassNote, inversion };
}

/**
 * Get the chord's interval data from a chord string.
 * Returns { root, intervals, maxInterval, bassNote, inversion }.
 *
 * - root: root note name (e.g. "A", "F#")
 * - intervals: semitone offsets from root for each chord tone (e.g. [0, 3, 7] for minor)
 * - maxInterval: highest interval, used to size the keyboard
 * - bassNote: bass note for slash chords (e.g. "B" for G/B), or null
 * - inversion: how many notes the symbol asks to be taken off the bottom (e.g. 1 for C^1), or 0
 */
export function getChordNotes(chordStr) {
  const parsed = parseChord(chordStr);

  if (!parsed) {
    return { root: chordStr, intervals: [0, 4, 7], maxInterval: 7, bassNote: null, inversion: 0 };
  }

  const { root, quality, bassNote, inversion } = parsed;
  const intervals = CHORD_INTERVALS[quality] ?? CHORD_INTERVALS.major;
  const maxInterval = Math.max(...intervals);

  // Validate that the root note is findable in its scale (defensive)
  const scale = getChromaticScale(root);
  const rootIdx = scale.indexOf(root);
  if (rootIdx === -1) {
    return { root, intervals: [0, 4, 7], maxInterval: 7, bassNote, inversion };
  }

  return { root, intervals, maxInterval, bassNote, inversion };
}

/**
 * Get the note name at a given semitone offset from a root note.
 * Wraps around at 12 for intervals beyond an octave.
 */
export function getNoteForInterval(root, semitones) {
  const scale = getChromaticScale(root);
  return getNoteAtInterval(root, semitones % 12, scale);
}
